/* eslint-disable max-lines-per-function */
import {
   AppointmentDetails,
   AppointmentDetailsQuery,
   AppointmentReceptionService,
   AppointmentReceptionServiceQuery,
} from '@dreryk/egabinet-appointment-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlement, VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';
import {
   AppointmentAdvancePayment,
   AppointmentAdvancePaymentQuery,
} from '@dreryk/egabinet-appointment-advance-payment-frontend-api';
import { CurrentHealthcareProviderPaymentsConfigurationEnabledQuery } from '@dreryk/egabinet-online-payments-integration-frontend-api';

import { isDemo } from '@dreryk/demo-variant-frontend';
import { useCqrsFind, useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { PromiseVoidFunction } from '@dreryk/react-common';
import { LoaderSpinner, Notification, NotificationType } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { calendarTranslationKeys } from '../../../../../translations';

type AppointmentDetailsFluidTabRenderProps = {
   appointmentDetails: AppointmentDetails;
   receptionServices: AppointmentReceptionService[];
   visitSettlement: VisitSettlement | null;
   appointmentAdvancePayment: AppointmentAdvancePayment | null;
   onlinePaymentsIntegrationExists: boolean;
   refetchReceptionServices: PromiseVoidFunction;
   refetchAppointmentDetails: PromiseVoidFunction;
   refetchVisitSettlement: PromiseVoidFunction;
   refetchAdvancePayment: PromiseVoidFunction;
};

type AppointmentDetailsFluidTabLoaderProps = {
   readonly appointmentId: number;
   readonly children: (renderProps: AppointmentDetailsFluidTabRenderProps) => JSX.Element;
};

export const AppointmentDetailsFluidTabLoader = (props: AppointmentDetailsFluidTabLoaderProps): JSX.Element | null => {
   const privateMedicalServiceAvailable = protectFeature('searchingPrivateMedicalServices');
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');

   const {
      data: appointmentDetails,
      error: appointmentDetailsError,
      loading: appointmentDetailsLoading,
      refetch: refetchAppointmentDetails,
   } = useCqrsGet<AppointmentDetails>(() => {
      const query = new AppointmentDetailsQuery();
      query.appointmentId = AppointmentId.of(props.appointmentId);
      return query;
   });

   const {
      data: receptionServices,
      error: receptionServicesError,
      fetching: receptionServiceFetching,
      refetch: refetchReceptionServices,
   } = useCqrsSearch<AppointmentReceptionService>(() => {
      const query = new AppointmentReceptionServiceQuery();
      query.appointmentId = AppointmentId.of(props.appointmentId);
      return query;
   });

   const {
      data: visitSettlement,
      loading: visitSettlementLoading,
      error: visitSettlementError,
      refetch: refetchVisitSettlement,
   } = useCqrsFind<VisitSettlement>(
      () => {
         const query = new VisitSettlementQuery();
         query.purchaseIds = [PurchaseId.forAppointment(AppointmentId.of(props.appointmentId))];
         return query;
      },
      { enabled: privateMedicalServiceAvailable }
   );

   const {
      data: appointmentAdvancePayment,
      loading: appointmentAdvancePaymentLoading,
      error: appointmentAdvancePaymentError,
      refetch: refetchAdvancePayment,
   } = useCqrsFind<AppointmentAdvancePayment>(
      () => {
         const query = new AppointmentAdvancePaymentQuery();
         query.appointmentIds = [AppointmentId.of(props.appointmentId)];
         return query;
      },
      {
         enabled: privateMedicalServiceAvailable || privateMedicalServicesReadOnlyAvailable,
      }
   );

   const {
      data: onlinePaymentsIntegrationExists,
      loading: onlinePaymentsIntegrationExistsLoading,
      error: onlinePaymentsIntegrationExistsError,
   } = useCqrsFind(() => new CurrentHealthcareProviderPaymentsConfigurationEnabledQuery(), { enabled: !isDemo() });

   if (
      appointmentDetailsLoading ||
      receptionServiceFetching ||
      visitSettlementLoading ||
      appointmentAdvancePaymentLoading ||
      onlinePaymentsIntegrationExistsLoading
   ) {
      return <LoaderSpinner />;
   }

   if (
      appointmentDetailsError ||
      receptionServicesError ||
      visitSettlementError ||
      appointmentAdvancePaymentError ||
      onlinePaymentsIntegrationExistsError
   ) {
      return (
         <Notification
            testId={'error'}
            type={NotificationType.ERROR}
            label={calendarTranslationKeys.appointmentContextDialog.tabs.appointmentDetailsTab.loader.fetchError}
         />
      );
   }

   return props.children({
      appointmentDetails: appointmentDetails,
      receptionServices: receptionServices,
      visitSettlement: visitSettlement,
      appointmentAdvancePayment: appointmentAdvancePayment,
      onlinePaymentsIntegrationExists: Boolean(onlinePaymentsIntegrationExists) ?? false,
      refetchReceptionServices: refetchReceptionServices,
      refetchAppointmentDetails: refetchAppointmentDetails,
      refetchVisitSettlement: refetchVisitSettlement,
      refetchAdvancePayment: refetchAdvancePayment,
   });
};
